import {
  ApiDateTime,
  CertificationType,
  CreateDocument,
  Document,
  DocumentCategory,
  DocumentId,
  DocumentMin,
  EsignatureTemplateId,
  OwnerId,
  ReviewPeriod,
  ScopeId,
  SharingClassification,
  SourcedFact,
  TrustCenterAccess,
  UpdateDocumentContent,
} from "platformed-client/types";
import {invalidateQueries, q} from "../../state"; // Updated import to include q
import {platformedClient} from "..";

export async function list(): Promise<DocumentMin[]> {
  return await platformedClient.internalRequest("get", `/region/{region}/api/vendor_toolkit/documents`, {});
}

export async function get(documentId: DocumentId): Promise<Document> {
  return await platformedClient.internalRequest("get", `/region/{region}/api/vendor_toolkit/documents/{document_id}`, {
    path: {document_id: documentId},
  });
}

export async function getFacts(documentId: DocumentId): Promise<SourcedFact[]> {
  return await platformedClient.internalRequest(
    "get",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/facts`,
    {path: {document_id: documentId}},
  );
}

export async function create(document: CreateDocument): Promise<Document> {
  const res = await platformedClient.internalRequest("post", `/region/{region}/api/vendor_toolkit/documents`, {
    body: document,
  });
  await invalidateQueries([q.vendorToolkit.documents.filter(), q.vendorToolkit.gettingStartedChecklist.filter()]);
  return res;
}

export async function updateNonAuthoritativeMarkdown(documentId: DocumentId, markdown: string) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/non_authoritative_markdown`,
    {body: markdown, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId), q.vendorToolkit.documents.filter()]);
  return res;
}

export async function updateReviewPeriod(documentId: DocumentId, reviewPeriod: ReviewPeriod) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/review_period`,
    {body: reviewPeriod, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId), q.vendorToolkit.documents.filter()]);
  return res;
}

export async function updateName(documentId: DocumentId, name: string) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/name`,
    {body: name, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId), q.vendorToolkit.documents.filter()]);
  return res;
}

export async function updateSharingClassification(
  documentId: DocumentId,
  sharing_classification: SharingClassification,
) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/sharing_classification`,
    {body: sharing_classification, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId), q.vendorToolkit.documents.filter()]);
  return res;
}

export async function updateCategory(documentId: DocumentId, category: DocumentCategory) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/category`,
    {body: category, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId), q.vendorToolkit.documents.filter()]);
  return res;
}

export async function assign(documentId: DocumentId, ownerId: OwnerId | null) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/assign`,
    {body: ownerId, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId), q.vendorToolkit.documents.filter()]);
  return res;
}

export async function updateContent(documentId: DocumentId, content: UpdateDocumentContent) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}`,
    {body: content, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId), q.vendorToolkit.documents.filter()]);
  return res;
}

export async function updateTrustCenterVisibility(documentId: DocumentId, visibility: TrustCenterAccess) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/trust_center_visibility`,
    {body: visibility, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId)]);
  return res;
}

export async function updateTrustCenterReadability(documentId: DocumentId, readability: TrustCenterAccess) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/trust_center_readability`,
    {body: readability, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId)]);
  return res;
}

export async function updateTrustCenterEsignatureTemplate(
  documentId: DocumentId,
  templateId: EsignatureTemplateId | null,
) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/trust_center_esignature_template`,
    {body: templateId, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId)]);
  return res;
}

export async function updateDocumentCertificationType(
  documentId: DocumentId,
  certificationType: CertificationType | null,
): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/certification_type`,
    {body: certificationType, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId)]);
  return res;
}

export async function updateDocumentCertificationDateGranted(
  documentId: DocumentId,
  certificationDateGranted: ApiDateTime | null,
): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/certification_date_granted`,
    {body: certificationDateGranted, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId)]);
  return res;
}

export async function updateDocumentCertificationDateExpires(
  documentId: DocumentId,
  certificationDateExpires: ApiDateTime | null,
): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/certification_date_expires`,
    {body: certificationDateExpires, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId)]);
  return res;
}

export async function updateDocumentCertificationAuditorUrl(
  documentId: DocumentId,
  certificationAuditorUrl: string | null,
): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/certification_auditor_url`,
    {body: certificationAuditorUrl, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId)]);
  return res;
}

export async function updateDocumentCertificationAuditReport(
  documentId: DocumentId,
  certificationAuditReport: DocumentId | null,
): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/certification_audit_report`,
    {body: certificationAuditReport, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId)]);
  return res;
}

export async function deleteDocument(documentId: DocumentId) {
  const res = await platformedClient.internalRequest(
    "delete",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}`,
    {path: {document_id: documentId}},
  );
  await invalidateQueries([
    q.vendorToolkit.documents.filter(),
    q.vendorToolkit.document.filter(documentId),
    q.vendorToolkit.documentSources.filter(),
    q.vendorToolkit.facts.filter(),
    q.vendorToolkit.factCounts.filter(),
    q.vendorToolkit.documentFacts.filter(),
  ]);
  return res;
}

export async function updateLimitedToScopes(documentId: DocumentId, scopeIds: ScopeId[] | null) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/limited_to_scopes`,
    {body: scopeIds, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId)]);
  return res;
}

export async function updateFactGenPrompt(documentId: DocumentId, factGenPrompt: string): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/prompt`,
    {body: factGenPrompt, path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId)]);
  return res;
}

export async function resetPrompt(documentId: DocumentId): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/prompt/reset`,
    {path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId)]);
  return res;
}

export async function reanalyze(documentId: DocumentId): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/documents/{document_id}/reanalyze`,
    {path: {document_id: documentId}},
  );
  await invalidateQueries([q.vendorToolkit.document.filter(documentId), q.vendorToolkit.documents.filter()]);
  return res;
}
